/**
 * Pure query helpers that operate on a Map of BlendFileEntry values.
 *
 * Every consumer that previously iterated `blendFile.objects` or
 * `blendFile.meshes` should call one of these instead, so cross-entry
 * iteration logic lives in a single place.
 */

import type { BlendObject } from "../blend-reader/types/BlendObject.ts";
import type { BlendShapeKeySet } from "../blend-reader/types/BlendShapeKey.ts";
import type { BlendFileEntry } from "./storeTypes.ts";

/** Yield every BlendObject across all loaded blend file entries. */
export function* allBlendObjects(
  blendFiles: ReadonlyMap<string, BlendFileEntry>,
): Generator<BlendObject> {
  for (const entry of blendFiles.values()) {
    yield* entry.file.objects;
  }
}

/**
 * Find the entry that owns a given object name.
 * Returns [entryId, entry] or undefined if not found.
 */
export function findEntryByObjectName(
  blendFiles: ReadonlyMap<string, BlendFileEntry>,
  name: string,
): [string, BlendFileEntry] | undefined {
  for (const [entryId, entry] of blendFiles) {
    for (const obj of entry.file.objects) {
      if (obj.name === name) return [entryId, entry];
    }
  }
  return undefined;
}

/** Collect all blend object names across all entries into a Set. */
export function allBlendObjectNames(
  blendFiles: ReadonlyMap<string, BlendFileEntry>,
): Set<string> {
  const names = new Set<string>();
  for (const obj of allBlendObjects(blendFiles)) {
    names.add(obj.name);
  }
  return names;
}

/**
 * Merge all entries' shapeKeySets into a single Map.
 * Keys are unique because addBlendFileParsed enforces name uniqueness.
 */
export function allShapeKeySets(
  blendFiles: ReadonlyMap<string, BlendFileEntry>,
): Map<string, BlendShapeKeySet> {
  const merged = new Map<string, BlendShapeKeySet>();
  for (const entry of blendFiles.values()) {
    for (const [key, sks] of entry.file.shapeKeySets) {
      merged.set(key, sks);
    }
  }
  return merged;
}

/** Find a BlendFileEntry by its SHA-256 hash. */
export function findEntryByHash(
  blendFiles: ReadonlyMap<string, BlendFileEntry>,
  hash: string,
): [string, BlendFileEntry] | undefined {
  for (const [entryId, entry] of blendFiles) {
    if (entry.hash === hash) return [entryId, entry];
  }
  return undefined;
}

/** Generate a unique blend file entry ID. */
export function generateBlendFileId(): string {
  return (
    "bf_" +
    Date.now().toString(36) +
    "_" +
    Math.random().toString(36).slice(2, 6)
  );
}

/**
 * Ensure every shape key set in the blend files has a corresponding entry
 * in the weights map. Sets missing from the map get default (zero) weights.
 *
 * This is the single place that reconciles saved weights (which omit
 * all-zero sets for compactness) with the actual shape key sets in the
 * blend file data.  Call it after resolving blend files during any scene
 * load path.
 */
export function ensureShapeKeyWeights(
  blendFiles: ReadonlyMap<string, BlendFileEntry>,
  weights: Map<string, Map<string, number>>,
): void {
  for (const entry of blendFiles.values()) {
    for (const [setName, skSet] of entry.file.shapeKeySets) {
      if (!weights.has(setName)) {
        const kw = new Map<string, number>();
        for (const key of skSet.keys) kw.set(key.name, key.defaultValue);
        weights.set(setName, kw);
      }
    }
  }
}

const OB_RESTRICT_VIEWPORT = 1;

/**
 * Compute visibility for blend objects, respecting restrict flags,
 * collection hiding, and parent propagation.  Existing entries in `vis`
 * are preserved (scene-level overrides take priority).
 */
export function initBlendVisibility(
  objects: BlendObject[],
  vis: Map<string, boolean>,
): void {
  const directlyHidden = new Set<string>();
  for (const obj of objects) {
    if (vis.has(obj.name)) continue; // preserve scene-level overrides
    if (
      (obj.restrictFlag & OB_RESTRICT_VIEWPORT) !== 0 ||
      obj.hiddenByCollection
    ) {
      directlyHidden.add(obj.name);
    }
  }
  // Propagate to children
  const childrenOf = new Map<string, string[]>();
  for (const obj of objects) {
    if (obj.parentName) {
      let list = childrenOf.get(obj.parentName);
      if (!list) {
        list = [];
        childrenOf.set(obj.parentName, list);
      }
      list.push(obj.name);
    }
  }
  const allHidden = new Set(directlyHidden);
  const propagate = (parent: string) => {
    const children = childrenOf.get(parent);
    if (!children) return;
    for (const child of children) {
      if (!vis.has(child)) allHidden.add(child);
      propagate(child);
    }
  };
  for (const h of directlyHidden) propagate(h);
  for (const obj of objects) {
    if (!vis.has(obj.name)) {
      vis.set(obj.name, !allHidden.has(obj.name));
    }
  }
}
